import { globalObject } from './globalObject'

/**
 * A global object that may carry Zone.js's symbol-based original-value storage.
 *
 * Zone.js patches many browser APIs and stores the originals under a hidden property whose
 * name is generated by `Zone.__symbol__`.
 *
 * @internal
 */
export interface BrowserWindowWithZoneJs {
  Zone?: {
    /**
     * Returns the hidden property name Zone.js uses to store the original (unpatched) value
     * for a given API name. Not all Zone.js versions expose this — treat it as optional.
     */
    __symbol__?: (name: string) => string
  }
}

/**
 * Returns the original (pre-Zone.js-patch) value of a property on `target`.
 *
 * Zone.js[1] patches many browser and JS platform APIs (e.g. `setTimeout`, `fetch`,
 * `addEventListener`) and stores the originals under a hidden property whose name is derived from
 * `Zone.__symbol__(name)`. In multiple cases Zone.js has been identified as the root cause of
 * serious issues such as memory leaks and high CPU usage. Using the original implementation
 * avoids those pitfalls.
 *
 * If Zone.js is not present, or if it has not patched `name`, the property is read directly from
 * `target` as a fallback.
 *
 * [1]: https://github.com/angular/angular/tree/main/packages/zone.js
 *
 * @internal
 * @param target - The object whose property should be retrieved (e.g. `globalObject`).
 * @param name - The property name to look up (e.g. `'setTimeout'`).
 * @returns The original unpatched value of `target[name]`.
 */
export function getZoneJsOriginalValue<Target, Name extends keyof Target & string>(
  target: Target,
  name: Name
): Target[Name] {
  const browserWindow = globalObject as BrowserWindowWithZoneJs
  let original: Target[Name] | undefined
  if (browserWindow.Zone && typeof browserWindow.Zone.__symbol__ === 'function') {
    original = (target as any)[browserWindow.Zone.__symbol__(name)]
  }
  if (!original) {
    original = target[name]
  }
  return original
}
